@if (fieldData().readonly) {
    <cipo-text-view [config]="fieldData()" [value]="controlValue()" [attr.data-testid]="fieldData().testId" />
} @else {
    <mat-form-field
        class="cipo--width--full"
        [attr.data-testid]="fieldData().testId"
        [appearance]="fieldData().appearanceFill ? 'fill' : 'outline'"
        [ngClass]="{
            'cipo-mark-changed': fieldData().markChanged && valueChanged(),
        }"
        [class.cipo-input]="fieldData().appearanceFill"
        [title]="selectedOptionsValue() | translate">
        @if (fieldData().label) {
            <mat-label>{{ fieldData().label | translate }}</mat-label>
        }
        <mat-select
            [formControl]="control()"
            [multiple]="fieldData().multiple"
            [canSelectNullableOptions]="fieldData().canSelectNullableOptions"
            (opened)="onOpen()"
            (closed)="onClose()"
            [placeholder]="fieldData().placeholder | translate">
            @let multiSelect = fieldData().multiple;

            @if (multiSelect) {
                <mat-select-trigger>
                    @if (allOptionsSelected() && !fieldData().preventAllSelectedLabel) {
                        {{ fieldData().allSelectedLabel || "cipoFields.allSelected" | translate }}
                    } @else {
                        {{ firstOptionValue() | translate }}
                        @if (controlValue()?.length > 1) {
                            <span
                                translate="cipoFields.more"
                                [translateParams]="{ count: controlValue().length - 1 }"></span>
                        }
                    }
                </mat-select-trigger>
            }

            @if (!this.fieldData().hideSearch && deepFieldData.options()?.length > LIST_SEARCH_THRESHOLD) {
                <div class="search-field cipo--width--full">
                    <mat-form-field class="cipo-input">
                        <input
                            matInput
                            #searchInput
                            [placeholder]="'cipoFields.searchOption' | translate"
                            [formControl]="searchControl"
                            (keydown)="onSearchKeydown($event)" />
                        @if (searchControl.value) {
                            <button
                                mat-icon-button
                                matSuffix
                                [attr.aria-label]="'a11y.form.clearInput' | translate"
                                (click)="searchControl.setValue('')">
                                <mat-icon aria-hidden="true">clear</mat-icon>
                            </button>
                        }
                    </mat-form-field>
                </div>
            }

            <div cdkVirtualScrollingElement [style.height.px]="virtualScrollingHeight()">
                @if (!deepFieldData.options()) {
                    <mat-option disabled>{{ "general.loadingMessage" | translate }}</mat-option>
                } @else if (filteredOptions().length === 0) {
                    <mat-option disabled>{{ "notFound.nothingFound" | translate }}</mat-option>
                } @else if (!searchControl.value) {
                    <!-- Display 'all' option only if the search is empty-->
                    @if (multiSelect) {
                        @if (!allOptionsDisabled() && !fieldData().toggleAllOutside) {
                            <div
                                (click)="toggleSelectAllOptions(!allOptionsSelected())"
                                (keydown.enter)="toggleSelectAllOptions(!allOptionsSelected())"
                                (keydown.space)="toggleSelectAllOptions(!allOptionsSelected())"
                                role="checkbox"
                                [attr.aria-checked]="allOptionsSelected()"
                                tabindex="0"
                                class="cipo--cursor--pointer mat-mdc-option p-0">
                                <mat-checkbox
                                    class="m-x-xs cipo--width--full"
                                    [checked]="allOptionsSelected()"
                                    [indeterminate]="!!controlValue()?.length && !allOptionsSelected()">
                                    {{ "cipoFields.all" | translate }}
                                </mat-checkbox>
                            </div>
                        }
                    } @else if (!deepFieldData.required() && !fieldData().hideNullOption) {
                        @let noneLabel = fieldData().noneLabel || "cipoFields.all" | translate;
                        <mat-option [value]="null" [title]="noneLabel">{{ noneLabel }}</mat-option>
                    }
                }
                @if (multiSelect) {
                    <cdk-virtual-scroll-viewport [itemSize]="ITEM_SIZE" minBufferPx="480" maxBufferPx="720">
                        <mat-option
                            *cdkVirtualFor="let option of filteredOptions(); trackBy: trackByOptionKey"
                            [value]="option.key"
                            [disabled]="option.isDisabled"
                            [title]="option.description || option.value | translate">
                            <span>{{ option.value | translate }}</span>
                            @if (option.isUsed && !fieldData().hideInUse) {
                                <span class="cipo-badge-label">{{ "cipoFields.inUse" | translate }}</span>
                            }
                        </mat-option>
                    </cdk-virtual-scroll-viewport>
                } @else {
                    <!-- Render all options for single select, as the select is not working properly without multiple select
                     Instead of checking the value, it checks the id (I guess).
                     Later edit: Tried setting the option.key as id, but still doesn't work -->
                    @for (option of filteredOptions(); track $index) {
                        <mat-option
                            [value]="option.key"
                            [disabled]="option.isDisabled"
                            [title]="option.description || option.value | translate">
                            @if (fieldData().optionViewType === CipoListOptionViewType.FLAG) {
                                <span class="fi fi-{{ option.key.toLowerCase() }}" style="margin-right: 8px"></span>
                            }
                            <span>{{ option.value | translate }}</span>
                            @if (option.isUsed && !fieldData().hideInUse) {
                                <span class="cipo-badge-label">{{ "cipoFields.inUse" | translate }}</span>
                            }
                        </mat-option>
                    }
                }
            </div>

            <!-- This is a workaround to display the trigger when virtual scroll is not properly added -->
            @if (multiSelect) {
                @for (item of controlValue(); track item) {
                    <mat-option style="display: none" [value]="item"></mat-option>
                }
            }
        </mat-select>
        @if (fieldData().toggleAllOutside && fieldData().multiple && !allOptionsDisabled() && !fieldData().readonly) {
            <button
                matSuffix
                mat-icon-button
                type="button"
                [disabled]="control().disabled"
                (click)="toggleSelectAllOptions(!controlValue()?.length); $event.stopPropagation()"
                [attr.aria-label]="'a11y.form.toggleSelectAll' | translate"
                [title]="(controlValue()?.length ? 'cipoFields.deselectAll' : 'cipoFields.selectAll') | translate">
                <mat-icon aria-hidden="true">{{ toggleAllIcon() }}</mat-icon>
            </button>
        }
        @if (hasAddressesDataSource() && !multiSelect) {
            <button
                matSuffix
                mat-icon-button
                data-testid="cipo-select-address-button"
                [disabled]="!controlSingleValue()"
                [attr.aria-label]="'a11y.form.editLocation' | translate"
                (click)="openMap($event)">
                <mat-icon aria-hidden="true">edit_location</mat-icon>
            </button>
        }
        @if (fieldData().fieldDescription) {
            <mat-icon
                matSuffix
                class="material-icons-outlined cipo--cursor--help"
                [title]="fieldData().fieldDescription | translate">
                info
            </mat-icon>
        }
        @if (fieldData().hint) {
            <mat-hint>{{ fieldData().hint | translate }}</mat-hint>
        }
        <mat-error>
            {{ control().hasError("custom") ? control().errors.custom : ("errors.required" | translate) }}
        </mat-error>
    </mat-form-field>
}
